import { useEffect, type ReactNode } from "react";
import { CircleMarker, MapContainer, Marker, Popup, Polyline, TileLayer, useMap } from "react-leaflet";
import type { LatLngExpression, LatLngTuple } from "leaflet";
import L from "leaflet";
import type { Station } from "../api/types";

export const PAKISTAN_CENTER: LatLngTuple = [30.4, 69.3];
export const PAKISTAN_ZOOM = 6;

function FitBounds({ positions }: { positions: LatLngTuple[] }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length < 2) return;
    map.fitBounds(L.latLngBounds(positions), { padding: [48, 48], maxZoom: 10 });
  }, [map, positions]);
  return null;
}

function InvalidateSize() {
  const map = useMap();
  useEffect(() => {
    const id = window.setTimeout(() => map.invalidateSize(), 0);
    return () => window.clearTimeout(id);
  }, [map]);
  return null;
}

export function TrainMap({
  children,
  fitPositions,
  className,
}: {
  children?: ReactNode;
  fitPositions?: LatLngExpression[];
  className?: string;
}) {
  const tuples = (fitPositions ?? []).filter((p): p is LatLngTuple => Array.isArray(p) && p.length >= 2) as LatLngTuple[];

  return (
    <MapContainer center={PAKISTAN_CENTER} zoom={PAKISTAN_ZOOM} className={className ?? "leaflet-map"} scrollWheelZoom>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <InvalidateSize />
      {tuples.length >= 2 ? <FitBounds positions={tuples} /> : null}
      {children}
    </MapContainer>
  );
}

export function StationDots({ stations }: { stations: Station[] }) {
  return (
    <>
      {stations.map((s) => {
        if (typeof s.latitude !== "number" || typeof s.longitude !== "number") return null;
        if (!Number.isFinite(s.latitude) || !Number.isFinite(s.longitude)) return null;
        return (
          <CircleMarker
            key={s.station_id}
            center={[s.latitude, s.longitude]}
            radius={3}
            pathOptions={{ color: "#64748b", fillColor: "#94a3b8", fillOpacity: 0.85, weight: 1 }}
          >
            <Popup>
              <strong>{s.station_name}</strong>
              <div className="muted">{s.city}</div>
            </Popup>
          </CircleMarker>
        );
      })}
    </>
  );
}

export { Marker, Popup, Polyline };
